/**
 * Pure helpers of the Costs section (ticket 6ad5d51d).
 *
 * Every number shown comes from the server: these functions only format or derive what
 * the price list, the credit packages and the rate card sent. The price wording is the
 * subscription panel's (formatPrice, planPriceLabel, moduleAction), so the two lists read
 * the same and a missing price reads "Contact MedLea" in both.
 */
import {
  formatPrice,
  moduleAction,
  planPriceLabel,
} from '../RechargingStation/RechargingStationItems/Subscription'

const withNs = ([key, opts]) => (opts ? [`payments:${key}`, opts] : [`payments:${key}`])

/** Monthly price of a plan, as `t(...)` arguments. */
export const planLabel = (price, lang = 'en') => withNs(planPriceLabel(price, lang))

/**
 * Price of a module, as `t(...)` arguments. The free modules, active from sign-up (the
 * server marks them `included`, from SELF_SIGNUP_MODULES), say so instead of a price; the
 * others show their monthly fee, "free" for a declared zero, otherwise the contact.
 */
export const modulePriceLabel = (module, lang = 'en') => {
  if (module?.included) return ['costs:modules.includedAtSignup']
  const formatted = formatPrice(module?.price, lang)
  return withNs(moduleAction({ active: false, adminOnly: false, price: module?.price }, formatted))
}

/** Price of one credit inside a package, or null when the package is malformed. */
export const pricePerCredit = (pkg) => {
  if (!pkg || typeof pkg.amountCents !== 'number' || !(pkg.credits > 0)) return null
  return { amountCents: pkg.amountCents / pkg.credits, currency: pkg.currency }
}

/** Currency of the price list (ISO code, upper case), or null when nothing is priced. */
export const listCurrency = (priceList, packages) => {
  const prices = [
    ...(priceList?.plans || []).map((p) => p?.price),
    ...(priceList?.modules || []).map((m) => m?.price),
    ...(packages || []),
  ]
  const found = prices.find((p) => p && typeof p.currency === 'string' && p.currency)
  return found ? found.currency.toUpperCase() : null
}

/** True when the registry gives a plan-credit allotment to at least one plan. */
export const hasPlanCredits = (priceList) =>
  (priceList?.plans || []).some((p) => typeof p?.planCredits === 'number')

// Resources of the rate card the page knows how to name, in display order.
const RATE_RESOURCES = ['gpu', 'cpu_core']

/** The rate card as [{id, value}] in credits per hour; [] when it cannot be read. */
export const rateEntries = (card) => {
  if (!card || card.unit !== 'credits_per_hour' || !card.rates) return []
  return RATE_RESOURCES.filter(
    (id) => typeof card.rates[id] === 'number' && Number.isFinite(card.rates[id])
  ).map((id) => ({ id, value: card.rates[id] }))
}

/** A number for reading (rates, credits): at most three decimals, locale separators. */
export const formatNumber = (value, lang = 'en') =>
  new Intl.NumberFormat(lang, { maximumFractionDigits: 3 }).format(value)
